import {
  useState,
  useEffect,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Toolbar,
} from "@mui/material";

import {
  getWorkflow,
  getWorkflowWebhookConfig,
} from "../../api/agentApi";
import { getScheduleLabel } from "./CronScheduleBuilder";
import DetailItem from "../AgentDetail/DetailItem";
import { Workflow } from "./WorkflowTypes";
import WorkflowVisualization from "./WorkflowVisualization";
import { useUser } from "../UserContext.jsx";
import { PERMISSIONS } from "../../permissions";

export default function WorkflowDetailPage() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const { can } = useUser();
  const canUpdateWorkflows = Boolean(can?.(PERMISSIONS.WORKFLOWS_UPDATE));
  const canRunWorkflows = Boolean(can?.(PERMISSIONS.WORKFLOWS_RUN));
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduleConfig, setScheduleConfig] = useState<any>(null);
  const [webhookConfig, setWebhookConfig] = useState<any>(null);

  useEffect(() => {
    const fetchWorkflow = async (): Promise<void> => {
      if (!workflowId) return;
      try {
        const data: Workflow = await getWorkflow(workflowId);
        setWorkflow(data);
        if (data.schedule_config) {
          setScheduleConfig(data.schedule_config);
        }
        try {
          const webhookCfg = await getWorkflowWebhookConfig(workflowId);
          if (webhookCfg && webhookCfg.adapter_type) {
            setWebhookConfig(webhookCfg);
          }
        } catch {
          // Silently fail - not all workflows have webhooks
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkflow();
  }, [workflowId]);

  if (loading)
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  if (!workflow)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Workflow not found</Alert>
      </Box>
    );

  return (
    <Box sx={{ width: "100%" }}>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0,
          pt: 1,
          px: 3,
        }}
      >
        <Toolbar disableGutters sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h4" sx={{ pl: 0 }}>
            {workflow.name} (v{workflow.version})
          </Typography>
        </Toolbar>
        <Box></Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          {canUpdateWorkflows && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate("/workflow/intent-build", { state: { workflow } })}
              sx={{ borderRadius: "99px", textTransform: "none", px: 3 }}
            >
              Refine
            </Button>
          )}
          {canRunWorkflows && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/workflow/${workflowId}/run`)}
              sx={{ borderRadius: "99px", textTransform: "none", px: 3 }}
            >
              Run Workflow
            </Button>
          )}
        </Box>
      </Box>

      {/* DETAILS */}
      <Box sx={{ p: 3 }}>
        <Paper elevation={0} sx={{ p: 2 }}>
          <DetailItem
            label="Workflow Name"
            value={workflow.dag_definition.workflow_name || workflow.name}
          />
          <DetailItem label="Workflow ID" value={workflow.id} />
          <DetailItem
            label="Description"
            value={workflow.dag_definition.description || "-"}
          />
          <DetailItem
            label="Total Steps"
            value={workflow.dag_definition.steps?.length || 0}
          />

          {/* Schedule Trigger Details */}
          {scheduleConfig && scheduleConfig.enabled && (
            <>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ my: 1.5, color: "#334155" }}
              >
                Schedule Trigger
              </Typography>
              <DetailItem
                label="Schedule Type"
                value={
                  <Chip
                    label={getScheduleLabel(scheduleConfig.cron_expression)}
                    sx={{
                      bgcolor: "#94A3B8",
                      color: "#fff",
                      fontWeight: 500,
                      borderRadius: "9999px",
                      height: 24,
                      px: 1.5,
                    }}
                  />
                }
              />
              <DetailItem
                label="Cron Expression"
                value={scheduleConfig.cron_expression}
              />
              <DetailItem
                label="Status"
                value={
                  <Chip
                    label={scheduleConfig.enabled ? "Enabled" : "Disabled"}
                    sx={{
                      bgcolor: scheduleConfig.enabled ? "#43a047" : "#e0e0e0",
                      color: scheduleConfig.enabled ? "#fff" : "#333",
                      fontWeight: 500,
                      borderRadius: "9999px",
                      height: 24,
                      px: 1.5,
                    }}
                  />
                }
              />
              {scheduleConfig.last_run_at && (
                <DetailItem
                  label="Last Run"
                  value={new Date(scheduleConfig.last_run_at).toLocaleString()}
                />
              )}
            </>
          )}

          {/* Webhook Trigger Details */}
          {webhookConfig && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#F8FAFC",
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Webhook Trigger
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Chip
                  label={webhookConfig.adapter_type}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={webhookConfig.enabled ? "Active" : "Inactive"}
                  size="small"
                  color={webhookConfig.enabled ? "success" : "default"}
                />
              </Box>
              {webhookConfig.secrets_ref &&
                Object.entries(webhookConfig.secrets_ref).map(
                  ([name, ref]: [string, any]) => (
                    <Typography
                      key={name}
                      variant="body2"
                      sx={{ fontSize: "13px", color: "#64748B" }}
                    >
                      {name}:{" "}
                      {typeof ref === "object" && ref?.key_name
                        ? ref.key_name
                        : "(configured)"}
                    </Typography>
                  ),
                )}
              <Typography
                variant="caption"
                sx={{ color: "#94A3B8", mt: 1, display: "block" }}
              >
                Endpoint: POST /workflow/{workflowId}/webhook
              </Typography>
            </Box>
          )}

          {/* Workflow Visualization */}
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              px: 2,
              py: 1,
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: "#64748B", mb: 1 }}
            >
              Workflow Visualization
            </Typography>
            <Box sx={{ height: "clamp(280px, 45vh, 600px)", position: "relative" }}>
              <WorkflowVisualization dagDefinition={workflow.dag_definition} />
            </Box>
          </Paper>
        </Paper>
      </Box>
    </Box>
  );
}
